import { fetchPreSignedURL, fetchPreSignedURLForVideo, uploadFileToPresignedURL } from "./apiCalls";

function formatNumber(number) {
    if(number >= 1000000000) {
        const value = (number/1000000000);
        return String(value.toFixed(1)) + 'b';
    }
    else if(number >= 1000000) {
        const value = (number/1000000);
        return String(value.toFixed(1)) + 'm';
    }
    else if(number >= 1000) {
        const value = (number/1000);
        return String(value.toFixed(1)) + 'k';
    }
    else {
        return number.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).split('.')[0];
    }
}

function formatTotal(number, hasDecimal=false) {
    let formattedString =  number.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    return hasDecimal ? formattedString : formattedString.split('.')[0];
}

function formatTimeDifference(timestamp) {
    const parsedDate = new Date(timestamp);
    const currentDate = new Date();

    const timeDifferenceInSeconds = Math.floor((currentDate - parsedDate) / 1000);

    if (timeDifferenceInSeconds < 60) {
        if(timeDifferenceInSeconds < 0) {
            return `0s`
        }
        else {
            return `${timeDifferenceInSeconds}s`;
        }
    }
    else if (timeDifferenceInSeconds < 3600) {
        const minutes = Math.floor(timeDifferenceInSeconds / 60);
        return `${minutes}m`;
    }
    else if (timeDifferenceInSeconds < 86400) {
        const hours = Math.floor(timeDifferenceInSeconds / 3600);
        return `${hours}h`;
    }
    else if (timeDifferenceInSeconds < 31536000) {
        const days = Math.floor(timeDifferenceInSeconds / 86400);
        return `${days}d`;
    }
    else {
        const years = Math.floor(timeDifferenceInSeconds / 31536000);
        return `${years}y`;
    }
}

function formatDate(dateString) {
    if(!dateString) {
        return null;
    }
    const date = new Date(dateString);
    
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    const day = date.getDate();
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    
    return `${day} ${month} ${year}`;
}

function formatDateToIST(dateString) {
    const dateOptions = {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }

    const timeOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata',
    }

    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('en-IN', dateOptions) + ', ' + date.toLocaleTimeString('en-IN', timeOptions).replace(/\s/g, '').toUpperCase();

    return `${formattedDate} IST`;
}

function extractFrame(videoUrl, time) {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.src = videoUrl;
        video.crossOrigin = 'Anonymous';

        video.addEventListener('loadeddata', () => {
            video.currentTime = time;
        });

        video.addEventListener('seeked', () => {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const context = canvas.getContext('2d');
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL();
            resolve(dataUrl);
        });

        video.addEventListener('error', (err) => {
            reject(err);
        });
    });
}

const convertToHHMMSS = (val) => {
    const secNum = parseInt(val, 10);
    let hours = Math.floor(secNum / 3600);
    let minutes = Math.floor((secNum - hours * 3600) / 60);
    let seconds = secNum - hours * 3600 - minutes * 60;

    if (hours < 10) {
        hours = '0' + hours;
    }
    if (minutes < 10) {
        minutes = '0' + minutes;
    }
    if (seconds < 10) {
        seconds = '0' + seconds;
    }
    let time;

    if (hours === '00') {
        time = minutes + ':' + seconds;
    }
    else {
        time = hours + ':' + minutes + ':' + seconds;
    }
    return time;
};

function formatDuration(durationInSeconds) {
    const minutes = Math.floor(durationInSeconds / 60);
    const seconds = Math.floor(durationInSeconds % 60);
  
    if (minutes === 0) {
        return `${seconds}s`;
    }
    else if (seconds === 0) {
        return `${minutes}m`;
    }
    else {
        return `${minutes}m ${seconds}s`;
    }
}

async function getMediaUrl(file) {
    let { extension, fileType } = getFileExtensionAndType(file);

    if (fileType.includes("image") || extension === "gif") {
        file = await convertToWebP(file);
        extension = "webp";
        fileType = "image/webp";
    }

    try {
        const fileURL = await fetchPreSignedURL(extension, fileType);
        await uploadFileToPresignedURL(file, fileURL.preSignedUrl);

        const replacementDomain = fileType.includes("video")
            ? "https://videoserver.xenie.in"
            : "https://imageserver.xenie.in";

        const customUrl = fileURL.file.replace(
            "https://hyprcom-public.s3.ap-south-1.amazonaws.com",
            replacementDomain
        );

        return customUrl;
    } catch (err) {
        console.error("Error in getMediaUrl:", err);
        return null;
    }
}

async function convertToWebP(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = async (e) => {
            try {
                const img = new Image();

                img.onload = async () => {
                    const canvas = document.createElement("canvas");
                    canvas.width = img.width;
                    canvas.height = img.height;

                    const ctx = canvas.getContext("2d");
                    ctx.drawImage(img, 0, 0);

                    canvas.toBlob(
                        (blob) => {
                            if (!blob) return reject("WebP conversion failed");

                            const converted = new File([blob], file.name.replace(/\.[^.]+$/, ".webp"), {
                                type: "image/webp",
                                lastModified: file.lastModified
                            });

                            resolve(converted);
                        },
                        "image/webp",
                        1.0
                    );
                };

                img.onerror = reject;
                img.src = e.target.result;
            } catch (err) {
                reject(err);
            }
        };

        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

async function getVideoUrl(video) {
    const { extension, fileType } = getFileExtensionAndType(video);

    try {
        const fileURL = await fetchPreSignedURLForVideo(extension, fileType);
        await uploadFileToPresignedURL(video, fileURL.preSignedUrl);

        // Removed replacement domain + .replace logic
        return fileURL.file;

    } catch (err) {
        console.error("Error in getVideoUrl:", err);
        return null;
    }
}

const getFileExtensionAndType = (file)  => {
    if (file) {
        const fileName = file.name;
        const extension = fileName.split(".").pop();
        const fileType = file.type;
        return { extension, fileType };
    }
    else {
        return { extension: "", fileType: "" };
    }
}

const formatDescription = (text) => {
    const hashtagRegex = /#\w+/g;
    const linkRegex = /\b(?:https?:\/\/)?(?:www\.)?([\w.-]+\.\w{2,})(\/\S*)?\b/g;

    return text.replace(hashtagRegex, (hashtag) => 
        `<span class="text-[#1055EB] hover:underline cursor-pointer">${hashtag}</span>`
    ).replace(linkRegex, (link) => {
        const formattedLink = link.includes('http') ? link : `http://${link}`;
        return `<a href="${formattedLink}" target="_blank" class="text-[#1055EB] hover:underline cursor-pointer">${link}</a>`;
    })
    .replace(/\n/g, '<br/>');
};

const getInitials = (name) => {
    if (!name) return "";


    const initials = name
        .trim()
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0]?.toUpperCase() || "")
        .join("");

    return initials;
};

const verifyEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
}

function isLink(text) {
    try {
        new URL(text);
        return true;
    }
    catch (_) {
        return false;
    }
}

function isValidPhoneNumber(number) {
    const phonePattern = /^[1-9]\d{9}$/;
    return phonePattern.test(number);
}

const toCamelCase = (str) => {
    return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => {
        return index === 0 ? match.toLowerCase() : match.toUpperCase();
    }).replace(/\s+/g, '');
};

const camelCaseToReadable = (camelCase) => {
    return camelCase
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/^./, str => str.toUpperCase());
};

const loadImage = (file) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = URL.createObjectURL(file);
        img.onload = () => resolve(img);
        img.onerror = reject;
    });
};

function sortSizes(sizeList) {
    const sizeOrder = {
        "XXS": 0,
        "xxs": 0,
        "XS": 1,
        "xs": 1,
        "S": 2,
        "s": 2,
        "Small": 2,
        "small": 2,
        "M": 3,
        "m": 3,
        "Medium": 3,
        "medium": 3,
        "L": 4,
        "l": 4,
        "Large": 4,
        "large": 4,
        "XL": 5,
        "xl": 5,
        "XXL": 6,
        "xxl": 6,
        "2xl": 6,
        "2XL": 6,
        "XXXL": 7,
        "xxxl": 7,
        "3xl": 7,
        "3XL": 7,
        "4xl": 8,
        "4XL": 8,
        "5xl": 9,
        "5XL": 9,
        "6xl": 10,
        "6XL": 10,
        "7xl": 11,
        "7XL": 11,
    };

    let sortedList = sizeList.sort((a, b) => {
        const aValue = sizeOrder[a] !== undefined ? sizeOrder[a] : parseInt(a);
        const bValue = sizeOrder[b] !== undefined ? sizeOrder[b] : parseInt(b);
        return aValue - bValue;
    });

    return sortedList;
};

const getOrderStatus = (status) => {
    switch (status) {
        case 'IN_TRANSIST':
            return 'In Transit';
        case 'PENDING':
            return 'Pending';
        case 'DELIVERED':
            return 'Delivered';
        case 'CANCELLED':
            return 'Cancelled';
        case 'RETURN':
            return 'Return';
        case 'EXCHANGE':
            return 'Exchange';
        case 'OUT_FOR_DELIVERY':
            return 'Out for Delivery';
        case 'READY_FOR_PICKUP':
            return 'Ready for Pickup';
        default:
            return '';
    }
}

const getStatusColor = (status) => {
    switch (status) {
        case 'IN_TRANSIST':
            return 'text-[#065F46] bg-[#D1FAE5]';
        case 'PENDING':
            return 'text-[#92400E] bg-[#FEF3C7]';
        case 'DELIVERED':
            return 'text-[#1E40AF] bg-[#DBEAFE]';
        case 'CANCELLED':
            return 'text-[#991B1B] bg-[#FEE2E2]';
        case 'RETURN':
            return 'text-[#666666] bg-[#EAEAEA]';
        case 'EXCHANGE':
            return 'text-[#9D174D] bg-[#FCE7F3]';
        case 'OUT_FOR_DELIVERY':
            return 'text-[#A34809] bg-[#FFEAB5]';
        case 'READY_FOR_PICKUP':
            return 'text-[#5E3573] bg-[#E8DBF3]';
        default:
            return '';
    }
};

const getPaymentStatusColor = (status) => {
    switch (status) {
        case 'PENDING':
            return 'text-[#92400E]';
        case 'SUCCESSFUL':
            return 'text-[#208B4C]';
        case 'CANCELLED':
            return 'text-[#991B1B]';
        case 'FAILED':
            return 'text-[#666666]';
        case 'COD':
            return 'text-[#FF9900]';
        case 'REFUNDED':
            return 'text-[#1E40AF]';
        default:
            return '';
    }
}

const RETURN_STATUS_MAP = {
  PENDING:  { title: "Pending",  statusClass: "text-[#92400E] bg-[#FEF3C7]" },
  REJECTED: { title: "Rejected", statusClass: "text-[#991B1B] bg-[#FEE2E2]" },
  ACCEPTED: { title: "Accepted", statusClass: "text-[#065F46] bg-[#D1FAE5]" },
};

const getReturnOrderStatusUI = (status) => 
  RETURN_STATUS_MAP[status?.toUpperCase()] ?? { 
    title: "Unknown", 
    statusClass: "text-gray-700 bg-gray-100", 
};

function getRandomTime() {
    const startHour = 8;
    const endHour = 15;

    const hour = Math.floor(Math.random() * (endHour - startHour + 1)) + startHour;
    const minute = Math.floor(Math.random() * 60);
    const second = Math.floor(Math.random() * 60);

    const formattedHour = String(hour).padStart(2, '0');
    const formattedMinute = String(minute).padStart(2, '0');
    const formattedSecond = String(second).padStart(2, '0');

    return `${formattedHour}:${formattedMinute}:${formattedSecond}`;
}

function getNextDate(currentDate) {
    const date = new Date(currentDate);
    date.setDate(date.getDate() + 1);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

function formatBadge(badge) {
    return badge
        .toLowerCase()
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

function formatLocation(location) {
    let [locality, state] = location.split(' (');
    let locations = locality.split('_');
    locations.pop();

    const formattedLocations = locations.map(loc =>
        loc.replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
    );

    return `${formattedLocations.join(', ')} (${state}`;
}

const formatCouponType = (couponType) => {
    switch (couponType) {
        case 'PERCENTAGE_DISCOUNTS':
            return 'Percentage Discount';
        case 'FIXED_DISCOUNTS':
            return 'Fixed Discount';
        case 'BUYX_GETX':
            return 'Buy X, Get X';
        default:
            return '';
    }
}

function formatUserName(str) {
    if(!str) {
        return '';
    }

    return str.split(' ').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
}

function getCachedMediaUrl(url) {
    if (!url) return url;

    // If already using imageserver, don't modify
    if (url.includes("imageserver.xenie.in")) {
        return url;
    }

    return url.replace(
        "https://hyprcom-public.s3.ap-south-1.amazonaws.com/",
        "https://imageserver.xenie.in/"
    );
}

// Graph functions
function formatDateToDayMonth(dateString) {
    // eslint-disable-next-line no-unused-vars
    const [year, month, day] = dateString.split('-');
    const formattedDay = Number(day);
    return `${formattedDay}-${month}`;
}

function getPreviousSevenDays() {
    const dates = [];
    const today = new Date();
    
    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    for (let i = 1; i <= 7; i++) {
        const previousDay = new Date(today);
        previousDay.setDate(today.getDate() - i);
    
        const isoDateWithTime = previousDay.toISOString();
        const isoDateWithoutTime = isoDateWithTime.split('T')[0];
        const dayOfWeek = daysOfWeek[previousDay.getUTCDay()];
        
        dates.push({
            isoDateWithoutTime,
            dayOfWeek
        });
    }

    return dates;
}

function getPreviousMonthDates() {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    let previousMonth = currentMonth - 1;
    let yearOfPreviousMonth = currentYear;

    if (previousMonth < 0) {
        previousMonth = 11;
        yearOfPreviousMonth -= 1;
    }

    const daysInPreviousMonth = new Date(yearOfPreviousMonth, previousMonth + 1, 0).getDate();

    const datesArray = [];
    for (let day = 1; day <= daysInPreviousMonth; day++) {
        const date = new Date(yearOfPreviousMonth, previousMonth, day);
        const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        datesArray.push(formattedDate);
    }

    return datesArray;
}

async function urlToFile(url, filename, mimeType) {
    if (!url) return null;

    if (!url.includes("https://hyprcom-public.s3.ap-south-1.amazonaws.com/")) {
        url = url.replace(
            "https://imageserver.xenie.in/",
            "https://hyprcom-public.s3.ap-south-1.amazonaws.com/"
        );
    }

    const response = await fetch(url);
    const blob = await response.blob();

    return new File([blob], filename, { type: mimeType || blob.type });
}

function formatDateToMMDDYYYY(dateString) {
    if (!dateString) return null;

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null; 

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day   = String(date.getDate()).padStart(2, '0');
    const year  = date.getFullYear();

    return `${month} ${day} ${year}`;
};

function stripTimeFromDate(date) {
    if (!date) return null;

    const parsedDate = new Date(date);
    if (isNaN(parsedDate)) return null;

    return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
};

function sanitizeToPositiveInteger(input) {
  if (typeof input !== 'string') return '';

  const onlyDigits = input.replace(/\D/g, '');
  return onlyDigits.replace(/^0+/, '');
};

function isDateExpired(dateString) {
  const date = new Date(dateString);
  date.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return date < today;
};

// ✅ Helper: Format key into Title Case
function formatChartKeyToTitle(key) {
    return key
        .replace(/([A-Z])/g, ' $1')   // add space before caps
        .replace(/_/g, ' ')           // underscores → spaces
        .replace(/\s+/g, ' ')         // collapse spaces
        .trim()
        .replace(/^./, (str) => str.toUpperCase()); // capitalize first
}

function getSizeChartColumnWidth(key, category) {
    switch (key) {
        // 🔹 Always 1/5
        case "bust":
        case "heelToToe": // Fixed typo: was "heeltoToe"
            return "w-1/5";
        
        // 🔹 1/5 (others) or 1/9 (Clothing Set)
        case "hip":
        case "sleeveLength":
        case "waist":
        case "inseam":
        case "outseam":
            return category === "Clothing Set" ? "w-1/9" : "w-1/5";
        
        // 🔹 1/4 (Coat & Jacket) or 1/9 (Clothing Set) or 1/5 (others)
        case "sizeName":
        case "chest":
        case "frontLength":
        case "shoulder":
            if (category === "Coat & Jacket") return "w-1/4";
            if (category === "Clothing Set") return "w-1/9";
            return "w-1/5";
        
        // 🔹 Shoe & Sandals / High Heels
        case "UK":
        case "US":
        case "EURO":
        case "toFitFootLength":
            if (category === "Shoe & Sandals") return "w-1/4";
            if (category === "High Heels") return "w-1/5";
            return "w-1/5";
        
        // 🔹 Default fallback
        default:
            return "w-1/5";
    }
}

const validatePassword = (password) => {
    const passwordRegex =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
};

export {
    formatTotal,
    formatTimeDifference,
    formatDate,
    extractFrame,
    convertToHHMMSS,
    formatDuration,

    getMediaUrl,
    getVideoUrl,
    formatNumber,
    formatDescription,
    getInitials,
    verifyEmail,
    isLink,
    isValidPhoneNumber,
    toCamelCase,
    camelCaseToReadable,
    loadImage,
    formatDateToIST,
    sortSizes,

    getOrderStatus,
    getStatusColor,
    getReturnOrderStatusUI,
    getPaymentStatusColor,
    getRandomTime,
    getNextDate,
    formatBadge,
    formatLocation,

    formatCouponType,
    formatUserName,
    getCachedMediaUrl,

    getFileExtensionAndType,

    formatDateToDayMonth,
    getPreviousSevenDays,
    getPreviousMonthDates,
    urlToFile,

    formatDateToMMDDYYYY,
    stripTimeFromDate,
    sanitizeToPositiveInteger,
    isDateExpired,

    formatChartKeyToTitle,
    getSizeChartColumnWidth,

    validatePassword,
    convertToWebP,
}
